import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const TeamPerformanceChart = ({ data, metric = 'resolved' }) => {
  const getBarColor = (value, max) => {
    const percentage = (value / max) * 100;
    if (percentage >= 80) return 'var(--color-success)';
    if (percentage >= 60) return 'var(--color-warning)';
    if (percentage >= 40) return 'var(--color-accent)';
    return 'var(--color-error)';
  };

  const maxValue = Math.max(...data?.map(item => item?.[metric]));

  const formatTooltip = (value, name, props) => {
    const { payload } = props;
    return [
      <div key="tooltip" className="space-y-1">
        <p className="font-medium">{payload?.name}</p>
        <p className="text-sm">Resolved: {payload?.resolved}</p>
        <p className="text-sm">Assigned: {payload?.assigned}</p>
        <p className="text-sm">Avg Time: {payload?.avgTime}h</p>
        <p className="text-sm">Workload: {payload?.workload}%</p>
      </div>
    ];
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-light">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Team Performance</h3>
          <p className="text-sm text-muted-foreground">Individual analyst performance and workload distribution</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <span>Showing: {metric === 'resolved' ? 'Resolved Incidents' : 'Workload %'}</span>
        </div>
      </div>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="name" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              label={{ 
                value: metric === 'resolved' ? 'Incidents Resolved' : 'Workload %', 
                angle: -90, 
                position: 'insideLeft' 
              }}
            />
            <Tooltip 
              content={({ active, payload }) => {
                if (active && payload && payload?.length) {
                  return (
                    <div className="bg-popover border border-border rounded-lg p-3 shadow-medium">
                      {formatTooltip(payload?.[0]?.value, payload?.[0]?.name, payload?.[0])}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey={metric} radius={[4, 4, 0, 0]}>
              {data?.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getBarColor(entry?.[metric], maxValue)} 
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div className="text-center">
          <div className="w-3 h-3 bg-success rounded-full mx-auto mb-1"></div>
          <span className="text-muted-foreground">Excellent (80%+)</span>
        </div>
        <div className="text-center">
          <div className="w-3 h-3 bg-warning rounded-full mx-auto mb-1"></div>
          <span className="text-muted-foreground">Good (60-79%)</span>
        </div>
        <div className="text-center">
          <div className="w-3 h-3 bg-accent rounded-full mx-auto mb-1"></div>
          <span className="text-muted-foreground">Average (40-59%)</span>
        </div>
        <div className="text-center">
          <div className="w-3 h-3 bg-error rounded-full mx-auto mb-1"></div>
          <span className="text-muted-foreground">Needs Attention (&lt;40%)</span>
        </div>
      </div>
    </div>
  );
};

export default TeamPerformanceChart;